
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, XCircle, Clock, Shield, User, AlertTriangle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface PatronVerification {
  id: string;
  patronName?: string;
  verificationStatus: 'verified' | 'failed' | 'pending' | 'expired';
  timestamp: Date;
  confidenceScore?: number;
  deviceInfo?: string;
  sessionId: string;
}

interface VenueVerificationDisplayProps {
  onPatronVerified?: (patron: PatronVerification) => void;
  onPatronDenied?: (patron: PatronVerification) => void;
}

export const VenueVerificationDisplay = ({ 
  onPatronVerified, 
  onPatronDenied 
}: VenueVerificationDisplayProps) => {
  const [currentVerification, setCurrentVerification] = useState<PatronVerification | null>(null);
  const [verificationHistory, setVerificationHistory] = useState<PatronVerification[]>([]);
  const [isListening, setIsListening] = useState(true);

  // Simulate receiving verification results from patron devices
  useEffect(() => {
    if (!isListening) return;

    const interval = setInterval(() => {
      // Simulate random verification results for demo
      if (Math.random() > 0.8) { // 20% chance every 2 seconds
        const mockVerification: PatronVerification = {
          id: Math.random().toString(36).substr(2, 9),
          patronName: `Patron ${Math.floor(Math.random() * 1000)}`,
          verificationStatus: Math.random() > 0.3 ? 'verified' : 'failed',
          timestamp: new Date(),
          confidenceScore: Math.random() > 0.5 ? Math.floor(Math.random() * 20 + 80) : undefined,
          deviceInfo: 'iOS 17.1 / Safari',
          sessionId: Math.random().toString(36).substr(2, 16)
        };
        
        setCurrentVerification(mockVerification);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [isListening]);

  const handleAcceptEntry = () => {
    if (currentVerification) {
      onPatronVerified?.(currentVerification);
      setVerificationHistory(prev => [currentVerification, ...prev.slice(0, 9)]);
      setCurrentVerification(null);
    }
  };

  const handleDenyEntry = () => {
    if (currentVerification) {
      onPatronDenied?.(currentVerification);
      setVerificationHistory(prev => [
        { ...currentVerification, verificationStatus: 'failed' }, 
        ...prev.slice(0, 9)
      ]);
      setCurrentVerification(null);
    }
  };

  const handleDismiss = () => {
    setCurrentVerification(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified': return 'text-neon-green border-neon-green bg-green-500/10';
      case 'failed': return 'text-red-500 border-red-500 bg-red-500/10';
      case 'pending': return 'text-yellow-500 border-yellow-500 bg-yellow-500/10';
      case 'expired': return 'text-gray-500 border-gray-500 bg-gray-500/10';
      default: return 'text-gray-400 border-gray-400 bg-gray-400/10';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified': return <CheckCircle className="w-5 h-5" />;
      case 'failed': return <XCircle className="w-5 h-5" />;
      case 'pending': return <Clock className="w-5 h-5" />;
      case 'expired': return <AlertTriangle className="w-5 h-5" />;
      default: return <User className="w-5 h-5" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center gap-2">
            <Shield className="w-8 h-8 text-neon-purple" />
            <h1 className="text-3xl font-bold text-white">Venue Security Portal</h1>
          </div>
          <p className="text-gray-300">Privacy-First ID Verification System</p>
          
          <div className="flex justify-center gap-4">
            <Badge variant="outline" className="border-neon-green text-neon-green">
              System Online
            </Badge>
            <Badge variant="outline" className="border-neon-blue text-neon-blue">
              {isListening ? 'Listening for Verifications' : 'Paused'}
            </Badge>
          </div>
        </div>

        {/* Current Verification Alert */}
        <AnimatePresence>
          {currentVerification && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: -20 }}
              className="fixed inset-4 z-50 flex items-center justify-center"
            >
              <div className="cyber-glass border-neon-purple/50 p-8 rounded-xl max-w-md w-full">
                <div className="text-center space-y-6">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                  >
                    {getStatusIcon(currentVerification.verificationStatus)}
                  </motion.div>
                  
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">
                      ID Verification Result
                    </h2>
                    <Badge 
                      className={`text-lg px-4 py-2 ${getStatusColor(currentVerification.verificationStatus)}`}
                    >
                      {currentVerification.verificationStatus.toUpperCase()}
                    </Badge>
                  </div>

                  <div className="space-y-3 text-sm">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-left">
                        <p className="text-gray-400">Patron</p>
                        <p className="text-white">{currentVerification.patronName || 'Anonymous'}</p>
                      </div>
                      <div className="text-left">
                        <p className="text-gray-400">Time</p>
                        <p className="text-white">{currentVerification.timestamp.toLocaleTimeString()}</p>
                      </div>
                    </div>

                    {currentVerification.confidenceScore && (
                      <div className="text-center">
                        <p className="text-gray-400">Verification Confidence</p>
                        <p className="text-neon-cyan text-lg font-semibold">
                          {currentVerification.confidenceScore}%
                        </p>
                      </div>
                    )}

                    <div className="text-center text-xs text-gray-500 border-t border-neon-purple/20 pt-3">
                      Session: {currentVerification.sessionId}
                    </div>
                  </div>

                  <div className="space-y-3">
                    {currentVerification.verificationStatus === 'verified' ? (
                      <div className="space-y-2">
                        <Button 
                          onClick={handleAcceptEntry}
                          className="w-full bg-gradient-to-r from-neon-green to-green-600 hover:from-green-600 hover:to-neon-green text-black font-semibold"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Grant Entry
                        </Button>
                        <Button 
                          onClick={handleDenyEntry}
                          variant="outline"
                          className="w-full border-red-500 text-red-500 hover:bg-red-500/10"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Deny Entry
                        </Button>
                      </div>
                    ) : (
                      <Button 
                        onClick={handleDismiss}
                        variant="outline"
                        className="w-full border-gray-500 text-gray-400"
                      >
                        Acknowledge
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Dashboard */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* System Status */}
          <Card className="cyber-glass border-neon-purple/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Shield className="w-5 h-5" />
                System Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-black/30 rounded-lg">
                  <p className="text-2xl font-bold text-neon-green">
                    {verificationHistory.filter(v => v.verificationStatus === 'verified').length}
                  </p>
                  <p className="text-sm text-gray-400">Verified Today</p>
                </div>
                <div className="text-center p-3 bg-black/30 rounded-lg">
                  <p className="text-2xl font-bold text-red-500">
                    {verificationHistory.filter(v => v.verificationStatus === 'failed').length}
                  </p>
                  <p className="text-sm text-gray-400">Denied Today</p>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Privacy Compliance</span>
                  <span className="text-neon-green">✓ Active</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Data Retention</span>
                  <span className="text-neon-green">✓ Zero Storage</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Edge Processing</span>
                  <span className="text-neon-green">✓ Device Only</span>
                </div>
              </div>

              <Button
                onClick={() => setIsListening(!isListening)}
                variant={isListening ? "default" : "outline"}
                className="w-full"
              >
                {isListening ? 'Pause Verification System' : 'Resume Verification System'}
              </Button>
            </CardContent>
          </Card>

          {/* Recent Verifications */}
          <Card className="cyber-glass border-neon-purple/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Clock className="w-5 h-5" />
                Recent Verifications
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {verificationHistory.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No verifications yet today</p>
                ) : (
                  verificationHistory.map((verification) => (
                    <div 
                      key={verification.id}
                      className="flex items-center justify-between p-3 bg-black/30 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        {getStatusIcon(verification.verificationStatus)}
                        <div>
                          <p className="text-sm font-medium text-white">
                            {verification.patronName || 'Anonymous'}
                          </p>
                          <p className="text-xs text-gray-400">
                            {verification.timestamp.toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                      <Badge 
                        className={`text-xs ${getStatusColor(verification.verificationStatus)}`}
                      >
                        {verification.verificationStatus}
                      </Badge>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Privacy Information */}
        <Card className="cyber-glass border-neon-green/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Shield className="w-5 h-5 text-neon-green" />
              Privacy & Compliance Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-gray-300">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold text-neon-green mb-2">Data Handling</h4>
                <ul className="space-y-1 text-xs">
                  <li>• No biometric data stored on venue systems</li>
                  <li>• No ID images transmitted to venue</li>
                  <li>• Only verification result shared</li>
                  <li>• Automatic data deletion after verification</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-neon-green mb-2">Compliance</h4>
                <ul className="space-y-1 text-xs">
                  <li>• Australian Privacy Act 1988 compliant</li>
                  <li>• GDPR Article 25 - Data Protection by Design</li>
                  <li>• ISO 27001 security standards</li>
                  <li>• Regular security audits</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
